/**
 * Content Analysis Utilities
 * 
 * Helper functions to extract analytics properties from user-generated content.
 * Used to enrich post_created, comment_created, and answer_created events.
 */

/**
 * Analyze text content and extract analytics properties
 */
export function analyzeContent(text: string | null | undefined): {
  contentText: string;
  contentLength: number;
  wordCount: number;
  hasQuestionMark: boolean;
  hasLinks: boolean;
  linkCount: number;
  mentionCount: number;
  hashtagCount: number;
  hashtags: string[];
} {
  // Handle null/undefined
  const content = text || '';
  
  // Limit to 5000 characters for analytics
  const contentText = content.substring(0, 5000);
  
  // Basic metrics
  const contentLength = content.length;
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  
  // Question detection
  const hasQuestionMark = content.includes('?');
  
  // Link detection (http:// or https://)
  const linkRegex = /https?:\/\/[^\s]+/g;
  const links = content.match(linkRegex) || [];
  const hasLinks = links.length > 0;
  const linkCount = links.length;
  
  // Mention detection (@username)
  const mentionRegex = /@[\w-]+/g;
  const mentions = content.match(mentionRegex) || [];
  const mentionCount = mentions.length;
  
  // Hashtag detection (#hashtag)
  const hashtagRegex = /#[\w-]+/g;
  const hashtagMatches = content.match(hashtagRegex) || [];
  const hashtags = hashtagMatches.map(tag => tag.substring(1)); // Remove # prefix
  const hashtagCount = hashtags.length;
  
  return {
    contentText,
    contentLength,
    wordCount,
    hasQuestionMark,
    hasLinks,
    linkCount,
    mentionCount,
    hashtagCount,
    hashtags,
  };
}

/**
 * Extract purchase intent keywords from text
 * Used for sponsor matchmaking
 */
export function detectPurchaseIntent(text: string | null | undefined): {
  hasPurchaseIntent: boolean;
  purchaseIntentKeywords: string[];
  purchaseIntentScore: number;
} {
  const content = (text || '').toLowerCase();
  
  // Purchase intent keywords
  const keywords = [
    'vendor',
    'pricing',
    'price',
    'cost',
    'budget',
    'recommendation',
    'recommend',
    'alternative',
    'alternatives',
    'comparison',
    'compare',
    'solution',
    'software',
    'tool',
    'platform',
    'service',
    'provider',
    'looking for',
    'need',
    'searching for',
    'anyone know',
    'suggestions',
    'advice',
    'help',
    'demo',
    'trial',
    'free trial',
    'quote',
    'proposal',
  ];
  
  // Find matching keywords
  const foundKeywords = keywords.filter(keyword => content.includes(keyword));
  
  // Calculate score (0-100)
  const purchaseIntentScore = Math.min(100, foundKeywords.length * 10);
  
  return {
    hasPurchaseIntent: foundKeywords.length > 0,
    purchaseIntentKeywords: foundKeywords,
    purchaseIntentScore,
  };
}

/**
 * Detect competitor mentions in text
 * Used for sponsor matchmaking
 */
export function detectCompetitorMentions(
  text: string | null | undefined,
  competitorNames: string[] = []
): {
  hasCompetitorMention: boolean;
  competitorsMentioned: string[];
  competitorMentionCount: number;
} {
  const content = (text || '').toLowerCase();
  
  // Find mentioned competitors
  const mentioned = competitorNames.filter(competitor =>
    content.includes(competitor.toLowerCase())
  );
  
  return {
    hasCompetitorMention: mentioned.length > 0,
    competitorsMentioned: mentioned,
    competitorMentionCount: mentioned.length,
  };
}

/**
 * Detect problem statements in text
 * Phrases like "we are struggling with", "how do you solve", "challenge is"
 */
export function detectProblemStatement(text: string | null | undefined): {
  hasProblemStatement: boolean;
  problemStatementPhrases: string[];
} {
  const content = (text || '').toLowerCase();
  
  const problemPhrases = [
    'struggling with',
    'problem with',
    'issue with',
    'challenge',
    'difficult',
    'how do you',
    'how to',
    'can\'t figure out',
    'need help',
    'not working',
    'broken',
    'error',
    'failing',
    'stuck',
  ];
  
  const foundPhrases = problemPhrases.filter(phrase => content.includes(phrase));
  
  return {
    hasProblemStatement: foundPhrases.length > 0,
    problemStatementPhrases: foundPhrases,
  };
}

/**
 * Generate a unique ID for tracking purposes
 */
export function generateTrackingId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Check if user has viewed something before (using localStorage)
 */
export function isFirstView(key: string): boolean {
  if (typeof window === 'undefined') return false;
  
  const storageKey = `analytics_first_view_${key}`;
  const hasViewed = localStorage.getItem(storageKey);
  
  if (!hasViewed) {
    localStorage.setItem(storageKey, 'true');
    return true;
  }
  
  return false;
}

/**
 * Mark something as viewed (using localStorage)
 */
export function markAsViewed(key: string): void {
  if (typeof window === 'undefined') return;
  
  const storageKey = `analytics_first_view_${key}`;
  localStorage.setItem(storageKey, 'true');
}

/**
 * Calculate days since a date
 */
export function daysSince(date: Date | string): number {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - targetDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Calculate minutes since a date
 */
export function minutesSince(date: Date | string): number {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - targetDate.getTime();
  return Math.floor(diffMs / (1000 * 60));
}

/**
 * Sanitize content text (remove PII)
 */
export function sanitizeContent(text: string | null | undefined): string {
  if (!text) return '';
  
  let sanitized = text;
  
  // Remove email addresses
  sanitized = sanitized.replace(/[\w.-]+@[\w.-]+\.\w+/g, '[email]');
  
  // Remove phone numbers (basic patterns)
  sanitized = sanitized.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]');
  sanitized = sanitized.replace(/\b\(\d{3}\)\s*\d{3}[-.]?\d{4}\b/g, '[phone]');
  
  return sanitized;
}
